import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { coach } from '../config/coach';

const links = [
  { label: 'Results', id: 'results' },
  { label: 'Story', id: 'about' },
  { label: 'What You Get', id: 'what-you-get' },
  { label: 'Contact', id: 'contact' },
];

const Logo = ({ onClick }) => (
  <button onClick={onClick} data-testid="navbar-logo" className="text-left leading-none group">
    <span className="block font-display font-bold uppercase tracking-[0.14em] text-white text-lg sm:text-xl leading-[0.9]">
      {coach.logoTop}
    </span>
    <span className="block font-display uppercase tracking-[0.42em] text-white/45 text-[9px] sm:text-[10px] mt-0.5 transition-colors group-hover:text-white/70">
      {coach.logoBottom}
    </span>
  </button>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? 'bg-black/60 backdrop-blur-md border-b border-white/10' : 'bg-transparent border-b border-transparent'
        }`}
        data-testid="navbar"
      >
        <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
          <Logo onClick={() => scrollTo('hero')} />

          <div className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="relative text-sm text-white/60 hover:text-white transition-colors uppercase tracking-widest after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-white after:transition-all hover:after:w-full"
              >
                {l.label}
              </button>
            ))}
            <button onClick={() => scrollTo('apply')} data-testid="nav-apply-btn" className="btn-primary !py-2.5 !px-6 !text-xs">
              Apply
            </button>
          </div>

          <button
            className="md:hidden text-white p-2 -mr-2"
            onClick={() => setOpen(true)}
            data-testid="navbar-menu-open"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-black md:hidden flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="px-5 h-16 flex items-center justify-between border-b border-white/10">
              <Logo onClick={() => scrollTo('hero')} />
              <button onClick={() => setOpen(false)} data-testid="navbar-menu-close" className="text-white p-2 -mr-2" aria-label="Close menu">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1 }}
                  onClick={() => scrollTo(l.id)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="font-display uppercase text-4xl tracking-tight text-white/80 hover:text-white text-left py-2"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 * links.length + 0.1 }}
                onClick={() => scrollTo('apply')}
                data-testid="mobile-nav-apply-btn"
                className="btn-primary mt-6 w-full"
              >
                Apply for Coaching
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
